'use client';

import { useState, useMemo } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useJobs } from "@/hooks/job";
import { Job } from "@/types/career";
import { ArrowRight, MapPin, Search, X, ChevronRight, SlidersHorizontal } from "lucide-react";

function JobRow({ job, index }: { job: Job; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.03 }}
    >
      <Link
        href={`/career/${job.slug}`}
        className="group flex items-center gap-4 py-5 border-b border-black/[0.07] hover:bg-black/[0.03] transition-colors duration-150 rounded-sm -mx-2 px-2"
      >
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-brand-bg group-hover:text-black transition-colors leading-snug mb-1.5 3xl:text-[2rem]">
            {job.title}
          </h3>
          <div className="flex items-center gap-3 flex-wrap">
            {job.department && (
              <span className="text-xs text-gray-600 3xl:text-base">{job.department}</span>
            )}
            {job.department && job.location && (
              <span className="text-brand-muted-light text-base">·</span>
            )}
            {job.location && (
              <span className="flex items-center gap-1 text-xs text-gray-600 3xl:text-base">
                <MapPin className="h-3 w-3" />
                {job.location}
              </span>
            )}
            {job.employment_type && (
              <>
                <span className="text-brand-muted-light text-xs">·</span>
                <span className="text-xs text-brand-muted-light 3xl:text-base">{job.employment_type}</span>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-brand-muted-light group-hover:text-brand-bg/60 group-hover:translate-x-0.5 transition-all duration-150 shrink-0" />
      </Link>
    </motion.div>
  );
}

export default function CareerBoard({ initialJobs }: { initialJobs: Job[] }) {
  const { data } = useJobs(
    { order_by: "created_at", order_direction: "asc" },
    initialJobs
  );
  const jobs = Array.isArray(data) ? data : initialJobs;

  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [search, setSearch] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const departments = useMemo(() => {
    return ["All", ...Array.from(new Set(jobs.map((j: Job) => j.department).filter(Boolean)))];
  }, [jobs]);

  const departmentCounts = useMemo(() => {
    return jobs.reduce((acc: Record<string, number>, j: Job) => {
      if (j.department) acc[j.department] = (acc[j.department] || 0) + 1;
      return acc;
    }, {});
  }, [jobs]);

  const locations = useMemo(() => {
    const unique = Array.from(
      new Set(jobs.map((j: Job) => j.location?.trim()).filter((l) => l && l !== "All"))
    );
    return ["All", ...unique];
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job: Job) => {
      const matchesDept   = selectedDepartment === "All" || job.department === selectedDepartment;
      const matchesLoc    = selectedLocation   === "All" || job.location   === selectedLocation;
      const matchesSearch =
        !search ||
        job.title?.toLowerCase().includes(search.toLowerCase()) ||
        job.description?.toLowerCase().includes(search.toLowerCase()) ||
        job.department?.toLowerCase().includes(search.toLowerCase());
      return matchesDept && matchesLoc && matchesSearch;
    });
  }, [jobs, selectedDepartment, selectedLocation, search]);

  const hasActiveFilters = selectedDepartment !== "All" || selectedLocation !== "All" || search;
  const clearFilters = () => {
    setSelectedDepartment("All");
    setSelectedLocation("All");
    setSearch("");
  };

  return (
    <div className="bg-brand-text ">

      <section className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto text-center 3xl:pt-[80px] lg:px-0 px-4 pt-10 xl:pt-24">
        <motion.h1
          initial={{ y: 18 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="font-bold text-brand-bg leading-[1.05] tracking-[-0.03em] mb-6 text-4xl sm:text-5xl 3xl:text-[6rem]"
        >
          We have {jobs.length} open positions
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.16 }}
          className="text-brand-muted-light text-lg max-w-xl leading-relaxed mb-14 3xl:text-2xl mx-auto"
        >
          The future Global transfers and travel eSIMs.
        </motion.p>
      </section>

      <section className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto lg:px-0 px-6">
        <div className="lg:hidden mb-4 items-center justify-center flex">
          <button
            onClick={() => setMobileFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-2 global-border-radius border border-brand-bg/15 bg-white px-4 py-2.5 text-sm font-medium text-brand-bg/70 hover:text-brand-bg transition-colors"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Filters
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-brand-bg text-brand-text text-[10px] font-bold">
                {[selectedDepartment !== "All", selectedLocation !== "All", !!search].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        <div className="lg:grid lg:grid-cols-[260px_1fr] 3xl:grid-cols-[300px_1fr] lg:gap-12 3xl:gap-16">
          <AnimatePresence>
            {(mobileFiltersOpen || true) && (
              <aside className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-24 lg:self-start`}>
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.28 }}
                >
                  {/* Search */}
                  <div className="relative mb-8">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-bg/35 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search roles..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full rounded-full bg-white border border-brand-bg/[0.1] pl-9 pr-9 py-2.5 text-sm text-brand-bg placeholder-brand-bg/30 focus:outline-none focus:border-brand-bg/25 transition-all duration-200 3xl:text-lg"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch("")}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-brand-bg/30 hover:text-brand-bg/70 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mb-8">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-brand-bg/30 mb-3 3xl:text-base">
                      Filter by team
                    </p>
                    <div className="flex flex-col gap-1">
                      {departments.map((dept) => {
                        const isActive = selectedDepartment === dept;
                        const count = dept === "All"
                          ? jobs.length
                          : (departmentCounts[dept] ?? 0);
                        return (
                          <button
                            key={dept}
                            onClick={() => setSelectedDepartment(dept)}
                            className={`flex items-center justify-between w-full global-border-radius px-3 py-2 text-sm font-medium text-left transition-all duration-150 3xl:text-base ${
                              isActive
                                ? "text-brand-bg font-bold"
                                : "text-brand-bg/60 hover:bg-brand-bg/[0.06] hover:text-brand-bg"
                            }`}
                          >
                            <span>{dept === "All" ? "All teams" : dept}</span>
                            <span className={`text-xs font-semibold tabular-nums 3xl:text-xl ${isActive ? "text-brand-text/50" : "text-brand-bg/30"}`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {locations.length > 2 && (
                    <div className="mb-8">
                      <p className="text-sm font-bold uppercase tracking-[0.2em] text-brand-bg/30 mb-3 3xl:text-base">
                        Filter by location
                      </p>
                      <div className="flex flex-col gap-1">
                        {locations.map((loc) => {
                          const isActive = selectedLocation === loc;
                          return (
                            <button
                              key={loc}
                              onClick={() => setSelectedLocation(loc)}
                              className={`flex items-center gap-2 w-full global-border-radius px-3 py-2 text-sm font-medium text-left transition-all duration-150 3xl:text-base ${
                                isActive
                                  ? "text-brand-bg font-bold"
                                  : "text-brand-bg/60 hover:bg-brand-bg/[0.06] hover:text-brand-bg"
                              }`}
                            >
                              <MapPin className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-brand-text/60" : "text-brand-bg/30"}`} />
                              {loc === "All" ? "All locations" : loc}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Clear filters */}
                  <AnimatePresence>
                    {hasActiveFilters && (
                      <motion.button
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1.5 text-xs text-brand-muted-light hover:text-brand-bg/70 transition-colors font-medium 3xl:text-sm"
                      >
                        <X className="h-3 w-3" /> Clear all filters
                      </motion.button>
                    )}
                  </AnimatePresence>
                </motion.div>
              </aside>
            )}
          </AnimatePresence>

          <div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.32 }}
              className="flex items-center justify-between mb-2 pb-4 border-b border-brand-bg/[0.07]"
            >
            </motion.div>

            <AnimatePresence mode="wait">
              {filteredJobs.length > 0 ? (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {filteredJobs.map((job: Job, idx: number) => (
                    <JobRow key={job.slug} job={job} index={idx} />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-20 text-center"
                >
                  <div className="h-12 w-12 rounded-full bg-brand-bg/[0.05] border border-brand-bg/[0.1] flex items-center justify-center mx-auto mb-5">
                    <Search className="h-5 w-5 text-brand-bg/25" />
                  </div>
                  <p className="text-brand-bg font-semibold text-lg mb-2 3xl:text-xl">No roles found</p>
                  <p className="text-brand-muted-light text-sm mb-7 max-w-xs mx-auto 3xl:text-base">
                    Try adjusting your filters or search. New roles are added regularly.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 rounded-full border border-brand-bg/15 bg-white px-6 py-2.5 text-sm font-semibold text-brand-bg/60 hover:text-brand-bg transition-all duration-200"
                  >
                    Clear filters
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

          </div>
        </div>
      </section>

      <section className="max-w-5xl 3xl:max-w-[1432px] mx-auto px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className=" pt-16 flex flex-col items-center text-center"
        >
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-brand-bg/30 mb-5 3xl:text-base">
            Don&apos;t see your role?
          </p>
          <h3 className="text-3xl sm:text-[2.4rem] font-semibold text-brand-bg leading-[1.05] tracking-tight mb-4 3xl:text-5xl">
            Send us a speculative application
          </h3>
          <p className="text-gray-600 text-base leading-relaxed mb-8 max-w-md 3xl:text-xl 3xl:max-w-xl">
            We&apos;re always looking for exceptional talent. If you think you&apos;d be a great fit, we&apos;d love to hear from you.
          </p>
          <Link
            href="/contact-us"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-grey text-brand-card px-7 py-3 text-sm font-bold transition-all duration-200 3xl:text-xl 3xl:px-9 3xl:py-4"
          >
            Get in touch <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
